import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';

import DataTable from './DataTable';
import FormModal from './FormModal';
import { getAttractions, createAttraction, updateAttraction, deleteAttraction, getDestinations } from '../../../services/travelInventoryService';

const AttractionsManager = ({ searchQuery }) => {
  const [attractions, setAttractions] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [attractionsData, destinationsData] = await Promise.all([
        getAttractions(),
        getDestinations(),
      ]);
      setAttractions(attractionsData || []);
      setDestinations(destinationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attraction?')) return;
    
    try {
      await deleteAttraction(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting attraction:', error);
      alert('Failed to delete attraction');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateAttraction(editingItem?.id, formData);
      } else {
        await createAttraction(formData);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving attraction:', error);
      throw error;
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'location', label: 'Location', sortable: true },
    { key: 'entry_fee', label: 'Entry Fee', type: 'currency' },
    { key: 'is_free', label: 'Free Entry', type: 'boolean' },
    { key: 'average_rating', label: 'Rating', sortable: true },
  ];

  const formFields = [
    {
      name: 'destination_id',
      label: 'Destination',
      type: 'select',
      required: true,
      options: destinations?.map((d) => ({ value: d?.id, label: `${d?.name}, ${d?.country}` })),
    },
    { name: 'name', label: 'Attraction Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { name: 'short_description', label: 'Short Description', type: 'textarea' },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'latitude', label: 'Latitude', type: 'number', step: '0.0000001' },
    { name: 'longitude', label: 'Longitude', type: 'number', step: '0.0000001' },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: [
        { value: 'historical', label: 'Historical Site' },
        { value: 'museum', label: 'Museum' },
        { value: 'park', label: 'Park/Garden' },
        { value: 'monument', label: 'Monument' },
        { value: 'religious', label: 'Religious Site' },
        { value: 'viewpoint', label: 'Viewpoint' },
        { value: 'entertainment', label: 'Entertainment' },
        { value: 'nature', label: 'Nature' },
      ],
    },
    { name: 'entry_fee', label: 'Entry Fee', type: 'number', step: '0.01' },
    { name: 'currency', label: 'Currency', type: 'text', defaultValue: 'PHP' },
    { name: 'opening_hours', label: 'Opening Hours', type: 'text' },
    { name: 'image_url', label: 'Attraction Image', type: 'image-upload' },
    { name: 'image_alt', label: 'Image Alt Text', type: 'text' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    {
      name: 'features',
      label: 'Features',
      type: 'checkbox-group',
      options: [
        { value: 'wheelchair_accessible', label: 'Wheelchair Accessible' },
        { value: 'guided_tours', label: 'Guided Tours Available' },
        { value: 'audio_guide', label: 'Audio Guide' },
        { value: 'parking', label: 'Parking Available' },
        { value: 'restrooms', label: 'Restrooms' },
        { value: 'cafe', label: 'Cafe/Restaurant' },
        { value: 'gift_shop', label: 'Gift Shop' },
        { value: 'photography_allowed', label: 'Photography Allowed' },
        { value: 'family_friendly', label: 'Family Friendly' },
        { value: 'pet_friendly', label: 'Pet Friendly' },
      ],
    },
    { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  ];

  const filteredData = attractions?.filter((item) => {
    const matchesSearch = searchQuery
      ? item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true;
    return matchesSearch;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Attractions</h2>
          <p className="text-gray-600 mt-1">Manage tourist attractions and points of interest</p>
        </div>
        <Button iconName="Plus" onClick={handleCreate}>
          Add Attraction
        </Button>
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        title={editingItem ? 'Edit Attraction' : 'Add Attraction'}
        fields={formFields}
        initialData={editingItem}
      />
    </div>
  );
};

export default AttractionsManager;